
/**
 * This class collates a form with the user submitted entries.
 *
 * @export
 * @class FormSubmission
 */
export class FormSubmission {
  /**
   * Creates an instance of FormSubmission.
   * @param {string} identifier The identifier of the form that this submission applies to.
   * @param {array} fields An array of FormField objects.
   * @param {object} initialValues The keys correspond to fields in the array. The values are the initial values.
   * @memberof FormSubmission
   */
  constructor (identifier, fields, initialValues, initialMetadata) {
    this.version = 1
    this.identifier = identifier
    this.metadata = initialMetadata || {}
    this.fields = fields.reduce((values, field) => {
      if (field && field.identifier) {
        const key = field.identifier
        values[key] = field.defaultValue ? field.defaultValue : undefined
      }
      return values
    }, {})

    if (initialValues) {
      this.setValuesForFields(initialValues)
    }
  }

  /**
   * Adds a collection of key-value pairs to the submission.
   *
   * @param {*} object An object to add to the submission.
   * @memberof FormSubmission
   */

  setValuesForFields (initialValues) {
    for (const key in initialValues) {
      if (initialValues.hasOwnProperty(key)) {
        this.setValueForField(initialValues[key], key)
      }
    }
  }

  /**
   * Adds a collection of key-value pairs to the submission.
   *
   * @param {*} object An object to add to the submission.
   * @memberof FormSubmission
   */
  setMetadataWithObject (object) {
    Object.assign(this.metadata, object)
  }

  /**
   * Sets a value for a form.
   *
   * @param {any} value An object or array of objects to store for the field.
   * @param {string} identifier The identifier of the FormField that the value corresponds to.
   * @memberof FormSubmission
   */
  setValueForField (value, identifier) {
    this.fields[identifier] = value
  }

  /**
   * Returns the contents of a submission field.
   *
   * @param {any} identifier The identifier of the field.
   * @returns The contents of the field
   * @memberof FormSubmission
   */
  valueForField (identifier) {
    return this.fields[identifier]
  }

  /**
   * Clears the value for the field corresponding to the identifier.
   *
   * @param {string} identifier The identifier whose value should be cleared.
   * @memberof FormSubmission
   */
  clearField (identifier) {
    delete this.fields[identifier]
  }
}
